import { useState, useEffect } from "react";
import { t } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { AdeLoadingSpinner } from "@/components/AdeLoadingSpinner";

// Animated connection line component
const ConnectionLine = ({ status, direction }) => {
  if (status === 'pending') {
    return (
      <div className="flex-1 h-0.5 mx-3 relative overflow-hidden bg-gray-200 rounded-full">
        {/* Animated seeking line */}
        <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-blue-400 to-transparent animate-connection-seek" />
        {/* Pulsing dots */}
        <div className="absolute inset-0 flex items-center justify-around">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: '200ms' }} />
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: '400ms' }} />
        </div>
      </div>
    );
  }
  
  if (status === 'accepted') {
    return (
      <div className="flex-1 h-0.5 mx-3 relative bg-emerald-400 rounded-full">
        <div className="absolute left-1/2 -translate-x-1/2 -top-2.5 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-200">
          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </div>
      </div>
    );
  }
  
  if (status === 'declined') {
    return (
      <div className="flex-1 h-0.5 mx-3 relative bg-red-300 rounded-full">
        <div className="absolute left-1/2 -translate-x-1/2 -top-2.5 w-6 h-6 rounded-full bg-red-400 flex items-center justify-center shadow-lg shadow-red-200">
          <X className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </div>
      </div>
    );
  }
  
  return null;
};

export default function Requests() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  
  const fetchRequests = async () => {
    try {
      const [received, sent, history] = await Promise.all([
        api.get('/connections/requests'),
        api.get('/connections/sent'),
        api.get('/connections/history').catch(() => ({ data: { requests: [] } }))
      ]);
      setReceivedRequests(received.data.requests);
      setSentRequests(sent.data.requests);
      setHistoryRequests(history.data.requests || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRequests();
    // Refresh every 10 seconds for real-time feel
    const interval = setInterval(fetchRequests, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const handleAccept = async (requestId) => {
    setProcessingId(requestId);
    try {
      await api.post(`/connections/accept/${requestId}`);
      // Move to history with accepted status
      const req = receivedRequests.find(r => r.request_id === requestId);
      if (req) {
        setHistoryRequests(prev => [{ ...req, status: 'accepted', resolved_at: new Date().toISOString() }, ...prev]);
      }
      setReceivedRequests(reqs => reqs.filter(r => r.request_id !== requestId));
      toast.success("Connessione stabilita!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleDecline = async (requestId) => {
    setProcessingId(requestId);
    try {
      await api.post(`/connections/decline/${requestId}`);
      // Move to history with declined status
      const req = receivedRequests.find(r => r.request_id === requestId);
      if (req) {
        setHistoryRequests(prev => [{ ...req, status: 'declined', resolved_at: new Date().toISOString() }, ...prev]);
      }
      setReceivedRequests(reqs => reqs.filter(r => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    } finally {
      setProcessingId(null);
    }
  };
  
  const getAvatarRingClass = (isVerified) => {
    return isVerified ? 'ring-2 ring-offset-1 ring-[#D4A574]' : '';
  };
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffHours < 1) return "Adesso";
    if (diffHours < 24) return `${diffHours}h fa`;
    return "Ieri";
  };
  
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-300">...</div>
      </div>
    );
  }
  
  return (
    <div className="page-content bg-gradient-to-b from-white to-gray-50/30" data-testid="requests-page">
      {/* Header */}
      <div className="sticky top-0 safe-top z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Connessioni</h1>
      </div>
      
      {/* Tabs */}
      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="w-full justify-start px-4 pt-2 bg-transparent border-b border-gray-100 rounded-none h-auto pb-0 gap-4">
          <TabsTrigger 
            value="pending" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none pb-3 text-sm font-medium px-0"
          >
            In attesa
            {(receivedRequests.length + sentRequests.length) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 text-xs">
                {receivedRequests.length + sentRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="history"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none pb-3 text-sm font-medium px-0"
          >
            Cronologia
          </TabsTrigger>
        </TabsList>
        
        {/* Pending Requests */}
        <TabsContent value="pending" className="mt-0 px-4">
          {/* Received */}
          {receivedRequests.length > 0 && (
            <div className="py-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Ricevute</p>
              <div className="space-y-4">
                {receivedRequests.map((req) => (
                  <div 
                    key={req.request_id}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                    data-testid={`request-${req.request_id}`}
                  >
                    <div className="flex items-center">
                      {/* Their avatar */}
                      <button onClick={() => navigate(`/app/user/${req.from_user.user_id}`)}>
                        <Avatar className={`w-12 h-12 ${getAvatarRingClass(req.from_user.is_verified)}`}>
                          {req.from_user.picture ? (
                            <AvatarImage src={req.from_user.picture} alt={req.from_user.name} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-600">
                              {req.from_user.name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      </button>
                      
                      {/* Animated connection line */}
                      <ConnectionLine status="pending" direction="incoming" />
                      
                      {/* Your avatar (placeholder) */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">Tu</span>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">{req.from_user.name}</p>
                        {req.from_user.bio && (
                          <p className="text-sm text-gray-500 truncate max-w-[150px]">{req.from_user.bio}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleAccept(req.request_id)}
                          disabled={processingId === req.request_id}
                          className="h-9 px-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium shadow-lg shadow-emerald-200/50"
                        >
                          Accetta
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDecline(req.request_id)}
                          disabled={processingId === req.request_id}
                          className="h-9 px-4 rounded-full border-gray-200 text-gray-600"
                        >
                          Rifiuta
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Sent */}
          {sentRequests.length > 0 && (
            <div className="py-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Inviate</p>
              <div className="space-y-4">
                {sentRequests.map((req) => (
                  <div 
                    key={req.request_id}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                  >
                    <div className="flex items-center">
                      {/* Your avatar (placeholder) */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">Tu</span>
                      </div>
                      
                      {/* Animated connection line */}
                      <ConnectionLine status="pending" direction="outgoing" />
                      
                      {/* Their avatar */}
                      <button onClick={() => navigate(`/app/user/${req.to_user.user_id}`)}>
                        <Avatar className={`w-12 h-12 ${getAvatarRingClass(req.to_user.is_verified)}`}>
                          {req.to_user.picture ? (
                            <AvatarImage src={req.to_user.picture} alt={req.to_user.name} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-orange-100 to-pink-100 text-orange-600">
                              {req.to_user.name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      </button>
                    </div>
                    
                    <div className="mt-3">
                      <p className="font-semibold text-gray-900">{req.to_user.name}</p>
                      <p className="text-sm text-gray-400">{t("pendingResponse")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {receivedRequests.length === 0 && sentRequests.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center mb-4">
                <div className="w-8 h-0.5 bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 rounded-full animate-pulse" />
              </div>
              <p className="text-gray-500">{t("noPendingRequests")}</p>
            </div>
          )}
        </TabsContent>
        
        {/* History */}
        <TabsContent value="history" className="mt-0 px-4 py-4">
          {historyRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-gray-400">{t("noRecentHistory")}</p>
              <p className="text-xs text-gray-300 mt-1">{t("resolvedConnectionsHint")}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {historyRequests.map((req) => (
                <div 
                  key={req.request_id}
                  className="bg-white rounded-xl p-3 border border-gray-100 flex items-center gap-3"
                >
                  <Avatar className={`w-10 h-10 ${getAvatarRingClass(req.from_user?.is_verified)}`}>
                    {req.from_user?.picture ? (
                      <AvatarImage src={req.from_user.picture} alt={req.from_user?.name} />
                    ) : (
                      <AvatarFallback className="bg-gray-100 text-gray-500">
                        {req.from_user?.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{req.from_user?.name}</p>
                    <p className="text-xs text-gray-400">{formatTimeAgo(req.resolved_at || req.created_at)}</p>
                  </div>
                  
                  {req.status === 'accepted' ? (
                    <div className="flex items-center gap-1 text-emerald-500">
                      <Check className="w-4 h-4" />
                      <span className="text-xs font-medium">Connessi</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-red-400">
                      <X className="w-4 h-4" />
                      <span className="text-xs font-medium">Rifiutata</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
